import {
  hasFixableGap,
  PREVIEW_FRESHNESS,
  tallyNokReasons,
  type RunFeedPreviewResponse,
  type RunFeedPreviewVerdictItem,
  type RunSkuSummary,
} from "@nova-ai/shared"

type InspectionFilter = {
  verdict: "all" | "ok" | "nok"
  cohort: "fixable" | "excluded" | "set-aside"
  reason: string | null
  gapsOnly: boolean
}

/** Counts describe inspected membership; unloaded or unsafe members remain unknown. */
export function summarizeRunInspection(
  preview: RunFeedPreviewResponse | undefined,
  total: number
): RunSkuSummary {
  const items = preview?.verdictItems ?? []
  const isCurrent = preview?.freshness.kind === PREVIEW_FRESHNESS.current
  const active = items.filter((item) => !item.setAside)
  const nokItems = isCurrent ? active.filter((item) => item.verdict === "nok") : []
  const tally = tallyNokReasons(nokItems.map((item) => item.reasons))
  const ok = isCurrent ? active.filter((item) => item.verdict === "ok").length : 0
  const setAside = items.filter((item) => item.setAside).length
  const nok = nokItems.length + setAside
  return {
    total,
    ok,
    nok,
    pending: Math.max(0, total - ok - nok),
    nokFixable: tally.fixable,
    nokBlocked: tally.blocked,
    nokRecoverable: tally.recoverable,
    setAside,
    reasons: tally.reasons,
  }
}

/** Filter canonical classifications only. This never derives a new verdict. */
export function filterRunInspection(
  preview: RunFeedPreviewResponse | undefined,
  filter: InspectionFilter
): RunFeedPreviewResponse | undefined {
  if (!preview) {
    return undefined
  }
  const items = preview.verdictItems.filter((item) => matchesInspection(item, filter, preview))
  const selected = new Set(items.map((item) => item.configSku))
  const rows = preview.rows.filter((row) => selected.has(row.configSku))
  return {
    ...preview,
    verdictItems: items,
    rows,
    rowCount: rows.filter((row) => !row.isPlaceholder).length,
    configSkuCount: items.length,
    gaps: preview.gaps.filter((gap) => selected.has(gap.configSku)),
    verdictOnlyGaps: preview.verdictOnlyGaps.filter((gap) => selected.has(gap.configSku)),
    dropped: preview.dropped.filter((drop) => selected.has(drop.configSku)),
    droppedCount: items.reduce((total, item) => total + (item.droppedCount ?? 0), 0),
    offersNeededCount: preview.offersNeededCount === null ? null : countOffers(rows, true),
    offersAlreadyListedCount:
      preview.offersAlreadyListedCount === null ? null : countOffers(rows, false),
  }
}

function matchesInspection(
  item: RunFeedPreviewVerdictItem,
  filter: InspectionFilter,
  preview: RunFeedPreviewResponse
): boolean {
  const activeVerdict = preview.freshness.kind === PREVIEW_FRESHNESS.current ? item.verdict : null
  const verdict = item.setAside ? "nok" : activeVerdict
  if (filter.verdict !== "all" && verdict !== filter.verdict) {
    return false
  }
  if (filter.verdict === "nok") {
    if (filter.cohort === "set-aside") {
      return item.setAside
    }
    if (item.setAside) {
      return false
    }
    if (hasFixableGap(item.reasons) !== (filter.cohort === "fixable")) {
      return false
    }
    if (filter.reason && !item.reasons.some((reason) => reason.reason === filter.reason)) {
      return false
    }
  }
  return !filter.gapsOnly || filter.verdict === "nok" || hasFixableGap(item.reasons)
}

function countOffers(rows: RunFeedPreviewResponse["rows"], needsOffer: boolean): number {
  return new Set(
    rows
      .filter((row) => !row.isPlaceholder && row.needsOffer === needsOffer)
      .map((row) => row.offerSku ?? row.simpleSku)
      .filter(Boolean)
  ).size
}
